/**
 * App-side wiring for `@heidi/runtime` doors (Wave 1 · P1).
 *
 * Packages are variant-agnostic (G1/F5): they never read `process.env` — this
 * module is where the app reads env/build-profile signals ONCE and injects the
 * resolved values. Imported at module scope near the top of `_app.tsx` so it
 * evaluates before feature modules that use the runtime doors.
 *
 * `buildProfile.ts` stays the app-side build-variant oracle (its
 * `NEXT_PUBLIC_*` reads are const-folded for on-prem DCE — do NOT move them
 * into a package; see .ai-context/onprem-guardrails.md F5 / ARCFE-664).
 */
import { pushDebugLog } from '@/features/performanceTracking/debug-log';
import {
  DESKTOP_SESSION_FLOW_FAILURE_CODES,
  SESSION_FLOW_FAILURE_TYPES,
} from '@/lib/telemetry/sessionFlowFailures';
import { installSentryBreadcrumbIpcGuard } from '@/tauri/sentry-breadcrumb-guard';
import { isTauri } from '@/tauri/utils';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { formatVersion } from '@/utils/releaseVersion';
import { editorVariantLogEnrich } from '@/utils/telemetry/editor-context';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { describeCallbackId } from '@heidi/plugin-core';
import { configureFeatureFlags } from '@heidi/runtime/feature-flags';
import { configureLogger, setTauriCallbackLabelResolver } from '@heidi/runtime/logger';
import {
  configureFrameSampler,
  installFrameSamplerObserver,
  registerClientPlatform,
  registerPerformanceDebugSink,
  registerSessionFlowFailureCodes,
  registerSessionFlowFailureTypes,
} from '@heidi/runtime/performance';
import { configurePlatform } from '@heidi/runtime/platform';
import { markStorageTelemetryReady } from '@heidi/runtime/storage';

// tauri-plugin-sentry injects before page JS; wrap its beforeBreadcrumb so
// a cyclic console/DOM payload cannot throw in invoke JSON.stringify.
installSentryBreadcrumbIpcGuard();

// Carries the dev-time misconfiguration guard that used to live inside
// `isTauri()` (src/tauri/utils.ts): a non-production Tauri WebView without
// NEXT_PUBLIC_IS_TAURI=true fails loudly here, at bootstrap.
configurePlatform({
  tauriDevOverride: process.env.NEXT_PUBLIC_IS_TAURI === 'true',
  isDev: getEnvironment() !== ENV_NAME.PROD,
});

// TELEMETRY-GATE: `enabled` is the explicit adapter switch — a suppressed
// build (`isSaasTelemetryEnabled` const-folds to false) wires the logger
// disabled, so no log leaves the appliance even if a Datadog token leaks into
// the deploy env. Token presence keeps its pre-move role, but only as one
// input to the explicit flag. `datadogLogs.init` itself stays in tracing.ts
// (F1-gated).
configureLogger({
  enabled: isSaasTelemetryEnabled && !!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
  isProd: getEnvironment() === ENV_NAME.PROD,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
  release: formatVersion(
    process.env.NEXT_PUBLIC_RELEASE_REF,
    process.env.NEXT_PUBLIC_RELEASE_SHA
  ),
  bundleId: process.env.NEXT_PUBLIC_BUNDLE_ID,
  gitCommit: process.env.NEXT_PUBLIC_GIT_COMMIT,
  deploymentId: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID,
  enrich: editorVariantLogEnrich,
});

// MUST stay immediately below configureLogger. Storage migrations run at the
// module evaluation of the atom modules that declare them, hundreds of
// modules before this file is reached in the eager `_app` graph, so every
// storage event they emit is produced while the logger door is still on its
// safe default with Datadog transmission off. `@heidi/runtime/storage` holds
// those events; this releases them through the now-configured logger.
// Datadog's own SDK buffers pre-`init` log calls, so flushing here rather
// than after `initDatadog()` still reaches the backend.
markStorageTelemetryReady();

// Names the anonymous callback id in Tauri's "[TAURI] Couldn't find callback
// id <n>" warning, which is the highest-volume log line in the desktop
// service and today says nothing about what was orphaned. The interceptor is
// the only place that string is seen; the registry is app-side because only
// the code that mints an id knows its name. Additive: an id nobody registered
// resolves to undefined and the warning is forwarded exactly as before.
setTauriCallbackLabelResolver(describeCallbackId);

// G1/F5 (P3 F3): the module-load NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS
// read becomes an injected boolean, honored by the door's read paths at one
// choke point (boolean flags only — there is no universal "enabled" value for
// string flags). The remaining app-side readers of this env var keep their
// reads legally (app code is the designated env zone) and converge on the
// door as they migrate. Flag PROVIDER registration is separate wiring —
// `registerFlagProviders` in `_app.tsx` (the door has no registration API).
configureFeatureFlags({
  forceEnableAll: process.env.NEXT_PUBLIC_FORCE_ENABLE_ALL_FEATURE_FLAGS === 'true',
});

// G1/F5: the `./performance` door emits tracking lifecycle events but owns no
// sink. `pushDebugLog` holds the NEXT_PUBLIC_PERF_DEBUG read and the jotai atom
// that only the app's PerfDebugPanel renders, so it stays app-side and is
// injected here. Runs before any component mounts, so no interaction-driven
// tracking call can precede it.
registerPerformanceDebugSink(pushDebugLog);

// The `./performance` door derives the client session-flow metric from pause
// and resume actions. The client platform and the error-name to semantic-type
// map are Scribe-domain values, so the app injects them here.
registerClientPlatform(isTauri() ? 'desktop' : 'web');
registerSessionFlowFailureTypes(SESSION_FLOW_FAILURE_TYPES);
// Registered on web too: it is keyed by Rust error codes no browser rejection
// carries, so it is inert there and there is no platform branch to drift.
registerSessionFlowFailureCodes(DESKTOP_SESSION_FLOW_FAILURE_CODES);

// Frame sampler (ARD-1977): a developer tool for now, with no production
// rollout and no feature flag. It exists only under the perf debug panel
// (`NEXT_PUBLIC_PERF_DEBUG=true`, never set in a deployed build): there the
// gate is switched on with the 30 s gap and the per-hour ceiling lifted so
// every interaction reports, and the one global observer goes on `document`.
// Outside that branch nothing runs — no listeners, no rAF, no gate state — so
// a production build pays nothing for it. `NEXT_PUBLIC_*` is const-folded at
// build time, which is what makes the branch dead code there.
if (process.env.NEXT_PUBLIC_PERF_DEBUG === 'true') {
  configureFrameSampler({
    enabled: true,
    minAdmissionGapMs: 0,
    maxAdmissionsPerWindow: Number.POSITIVE_INFINITY,
  });
  if (typeof document !== 'undefined') installFrameSamplerObserver();
}
